import { Menu, MenuItem } from '@mui/material';
import { SvgIconTypeMap } from '@mui/material/SvgIcon/SvgIcon';
import classNames from 'classnames';
import React, { useState, useRef } from 'react';
import EngagementPlanDefaultImage from 'assets/images/EngagementPlanDefaultImage.jpg';
import EngagementPlanDefaultImage2 from 'assets/images/EngagementPlanDefaultImage2.png';
import { DotsThreeIcon } from 'components/Icons/DotsThreeIcon';
import { StatusLabel, AVAILABLE_STATUS_LABELS } from 'components/StatusLabel/StatusLabel';
import { Text } from 'components/Typography';
import { parseDateStringIgnoreTimezone } from 'utils/time';
import style from './EngagementPlanCard.scss';

interface EngagementPlanCardProps {
  title: string;
  icon?: SvgIconTypeMap['props']['children'];
  backgroundImage?: string;
  onClick?: () => void;
  className?: string;
  // New properties for engagement plan data
  startDate?: string;
  adherencePercent?: number;
  completedTaskCount?: number;
  totalTaskCount?: number;
  durationDays?: number;
  status?: string;
  isAddCard?: boolean; // Flag to identify the "Add Engagement Plan" card
  planId?: string; // Patient engagement plan ID for cancel API
  onCancel?: (planId: string) => void; // Callback for cancel action
  onDetails?: (planId: string) => void; // Callback for details action
  disabled?: boolean; // Flag to disable the card
  isGlobal?: boolean;
}

export const EngagementPlanCard: React.FC<EngagementPlanCardProps> = ({
  title,
  icon,
  backgroundImage,
  onClick,
  className,
  startDate,
  adherencePercent,
  completedTaskCount,
  totalTaskCount,
  durationDays,
  status,
  isAddCard = false,
  planId,
  onCancel,
  onDetails,
  disabled = false,
  isGlobal = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement>(null);
  // Format start date - use timezone-safe parsing
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = parseDateStringIgnoreTimezone(dateString);
    if (!date) return dateString; // fallback to original string if parsing fails

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate progress bar width
  const progressWidth = adherencePercent ? `${Math.min(Math.max(adherencePercent, 0), 100)}%` : '0%';
  const imageLink = !isGlobal ? backgroundImage || EngagementPlanDefaultImage2 : EngagementPlanDefaultImage;

  // Calculate current day for active plans - use timezone-safe parsing
  const calculateCurrentDay = (startDateString?: string) => {
    if (!startDateString) return 1;
    const startDate = parseDateStringIgnoreTimezone(startDateString);
    if (!startDate) return 1;

    const today = new Date();
    const timeDiff = today.getTime() - startDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1; // +1 because day 1 is the start date
    return Math.max(1, daysDiff);
  };

  const currentDay = calculateCurrentDay(startDate);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleDetailsClick = () => {
    setMenuOpen(false);
    if (onDetails && planId) {
      onDetails(planId);
    }
  };

  const handleCancelClick = () => {
    setMenuOpen(false);
    if (onCancel && planId) {
      onCancel(planId);
    }
  };

  // If this is the "Add Engagement Plan" card, use the original design
  if (isAddCard) {
    return (
      <div
        className={classNames(style.card, style.cardInteractive, style.addCard, { [style.disabled]: disabled }, className)}
        onClick={disabled ? undefined : onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={disabled ? 'Add Engagement Plan (disabled - patient device setup required)' : 'Add Engagement Plan'}
        aria-describedby={disabled ? 'device-setup-required' : undefined}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      >
        <div className={style.content}>
          {icon && <div className={style.icon}>{icon}</div>}
          <Text variant="lg" weight={500} className={style.title}>
            {title}
          </Text>
        </div>
      </div>
    );
  }

  // New engagement plan card design
  return (
    <div className={classNames(style.card, style.engagementPlanCard, className)}>
      {/* Background image section with overlay title */}
      <div className={style.imageSection} style={{ backgroundImage: `url(${imageLink})` }}>
        {/* Status tag */}
        {status && (
          <StatusLabel status={status === 'ACTIVE' ? AVAILABLE_STATUS_LABELS.Active : AVAILABLE_STATUS_LABELS.Completed} label={status === 'ACTIVE' ? 'Active' : 'Completed'} />
        )}
        <div className={style.titleOverlay}>
          <span className={style.overlayTitle}>{title}</span>
        </div>
      </div>

      {/* Content section */}
      <div className={style.contentSection}>
        {/* Start date with menu */}
        {startDate && (
          <div className={style.startDate}>
            <div className={style.startDateContent}>
              <span className={style.startLabel}>Start</span>
              <span className={style.startDateText}>{formatDate(startDate)}</span>
              <span className={style.dateDot}>•</span>
              <span className={style.statusText}>{status === 'ACTIVE' ? `Day ${Math.min(currentDay, durationDays || currentDay)} of ${durationDays || 'N/A'}` : 'Completed'}</span>
            </div>
            <div className={style.menuContainer}>
              <div ref={menuAnchorRef} className={style.menuButton} onClick={handleMenuClick}>
                <DotsThreeIcon width={16} height={16} />
              </div>
              <Menu
                anchorEl={menuAnchorRef.current}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem onClick={handleDetailsClick} className={style.menuItem}>
                  Details
                </MenuItem>
                {status === 'ACTIVE' && (
                  <MenuItem onClick={handleCancelClick} className={classNames(style.menuItem, style.cancelMenuItem)}>
                    Cancel
                  </MenuItem>
                )}
              </Menu>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className={style.progressContainer}>
          <div className={style.progressBar}>
            <div className={style.progressFill} style={{ width: progressWidth }} />
          </div>
        </div>
        {/* Adherence and task completion info */}
        <div className={style.statsContainer}>
          <span className={style.adherenceText}>{adherencePercent !== undefined ? `${Math.round(adherencePercent)}% adherence` : ''}</span>
          <span className={style.tasksText}>
            {completedTaskCount !== undefined && totalTaskCount !== undefined ? `${completedTaskCount}/${totalTaskCount} tasks finished` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};
